<template>
    <div>
        <div class="flex flex-col xl:gap-48 lg:gap-32 lg:flex-row-reverse lg:items-start lg:justify-between">
            <OrderDetails class="lg:flex-1" />

            <div class="col lg:flex-1">
                <AppAccordion label="Create Account" prefix="1." :complete="store.$state.accountValidated" v-model="store.$state.steps.createAccount.open" ref="createAccountAccordion">
                    <LoginStep mode="new" @complete="handleLoginComplete" />
                </AppAccordion>

                <AppAccordion label="Select Equipment" prefix="2." class="border-t-2 border-gray-400" :disabled="!store.$state.steps.createAccount.complete" :complete="store.$state.steps.selectEquipment.complete" v-model="store.$state.steps.selectEquipment.open">
                    <HeadsetFinancing v-if="store.org?.features?.headsetFinancing" />
                    <EquipmentStep disableShippingOptions />
                </AppAccordion>

                <AppAccordion :label="cart.requireShipping ? 'Shipping &amp; Payment' : 'Payment'" prefix="3." class="border-t-2 border-gray-400" :disabled="!store.$state.steps.selectEquipment.complete" :complete="store.$state.steps.paymentMethod.complete" v-model="store.$state.steps.paymentMethod.open">
                    <StripeElements v-if="store.$state.steps.paymentMethod.open" :handler="paymentHandler" :validatorFn="prePaymentValidator" />
                </AppAccordion>
            </div>
        </div>

        <ProgressModal v-if="state.showProgressModal" :steps="steps" :loading="state.showProgressModal">
            <template v-if="state.orderComplete">
                <p class="text-center my-6">
                    <template v-if="redirectCountDown >= 0">
                        You will be redirected in {{ redirectCountDown }} seconds
                    </template>
                    <template v-else> Redirecting you now... </template>
                </p>
                <a class="button button--primary" :href="orderCompletePage">
                    Click here to Get Started!
                </a>
            </template>
        </ProgressModal>
    </div>
</template>

<script setup lang="ts">
import { useCheckoutStore } from '@/store/checkout'
import { useUserStore } from '@/store/user'
import { useCartStore } from '@/store/cart'
import OrderDetails from '@/components/Shared/OrderDetails.vue'
import LoginStep from '@/components/Shared/LoginStep.vue'
import EquipmentStep from '@/components/Shared/EquipmentStep.vue'
import AppAccordion from '@/components/App/AppAccordion.vue'
import { createMembership, createHardwareOrder, isUniqueEmail } from '@/api/memberships'
import { computed, nextTick, reactive, ref } from 'vue'
import { v4 as uuid } from 'uuid'
import ProgressModal from '@/components/Shared/ProgressModal.vue'
import StripeElements from '@/components/Shared/StripeElements.vue'
import { trackOrder } from '@/utils/trackers'
import HeadsetFinancing from '@/components/Hardware/HeadsetFinancing.vue'

const store = useCheckoutStore()
const userStore = useUserStore()
const cart = useCartStore()

const state = reactive({
    paymentComplete: false,
    subscriptionComplete: false,
    equipmentComplete: false,
    trackingComplete: false,
    finalizingComplete: false,
    orderComplete: false,
    trackingResponse: {
        tracked: [] as any[],
        errors: [] as unknown[],
    },
    redirectInterval: null as number | null,
    showProgressModal: false,
    org: null as Org | null,
})

const redirectCountDown = ref(5)
const createAccountAccordion = ref()
const orderCompletePage = computed(() => {
    return `/membership-purchased/?plan=${cart.selectedPlan?.group_key}&billing=${cart.selectedPlan?.billing_interval}&promocode=${cart.$state.couponPromo?.code}`
})

const steps = computed((): ProgressStep[] => {
    const steps: ProgressStep[] = [
        {
            name: 'Verifying payment',
            status: state.paymentComplete ? 'complete' : 'current',
        },
        {
            name: 'Processing order',
            status: !state.paymentComplete
                ? 'upcoming'
                : state.subscriptionComplete
                    ? 'complete'
                    : 'current',
        },
        {
            name: 'Creating login credentials',
            status: !state.equipmentComplete
                ? 'upcoming'
                : state.trackingComplete
                    ? 'complete'
                    : 'current',
        },
        {
            name: 'Order complete',
            status: !state.finalizingComplete
                ? 'upcoming'
                : state.orderComplete
                    ? 'complete'
                    : 'current',
        },
    ]
    if (cart.requireShipping) {
        steps.splice(2, 0, {
            name: 'Ordering equipment',
            status: !state.subscriptionComplete
                ? 'upcoming'
                : state.equipmentComplete
                    ? 'complete'
                    : 'current',
        })
    }
    return steps
})

const handleLoginComplete = () => {
    store.$patch({
        steps: {
            createAccount: {
                complete: true,
                open: false,
            },
            selectEquipment: {
                complete: false,
                open: true,
            },
        },
    })
}

const resetProgress = () => {
    state.paymentComplete = false
    state.equipmentComplete = false
    state.trackingComplete = false
    state.finalizingComplete = false
    state.orderComplete = false
    state.trackingResponse = {
        tracked: [] as any[],
        errors: [] as unknown[],
    }
    state.redirectInterval = null
    state.showProgressModal = false
}

const redirect = () => {
    window.location.href = orderCompletePage.value
}

const finalizeOrder = () =>
    new Promise((resolve) => {
        setTimeout(() => {
            state.trackingComplete = true
            setTimeout(() => {
                state.finalizingComplete = true
                setTimeout(() => {
                    state.orderComplete = true
                    state.redirectInterval = window.setInterval(() => {
                        if (redirectCountDown.value <= 1) {
                            if (state.redirectInterval) {
                                clearInterval(state.redirectInterval)
                            }
                            state.showProgressModal = false
                            redirect()
                        }
                        redirectCountDown.value--
                    }, 1000)
                    resolve(true)
                }, 350)
            }, 250)
        }, 150)
    })

const handleTracking = async(membershipOrder: any, equipmentOrder: any) => {
    const { tracked, errors } = trackOrder(membershipOrder, equipmentOrder)
    state.trackingResponse.tracked = tracked || []
    state.trackingResponse.errors = errors || []
}

const handleEquipmentPurchase = async(paymentMethodId: string, stripeCustomerId: string) => {
    const params = store.getCheckoutParams()
    const orderParams = {
        ...params,
        payment_method: paymentMethodId || store.$state.paymentMethodId,
        customer_id: stripeCustomerId || store.$state.stripeCustomerId,
        account_id: store.$state.accountId || undefined,
    }
    const orderResponse = await createHardwareOrder(orderParams)
    state.equipmentComplete = true
    return orderResponse
}

const handleSubscriptionPurchase = async(
    paymentMethodId: string,
    stripeCustomerId: string,
): Promise<MembershipOrder> => {
    const params = store.getCheckoutParams()
    const orderParams = {
        ...params,
        payment_method: paymentMethodId || store.$state.paymentMethodId,
        customer_id: stripeCustomerId || store.$state.stripeCustomerId,
    }

    const membershipOrder: MembershipOrder = await createMembership(orderParams)

    store.$patch({
        membershipOrder,
        stripeCustomerId: membershipOrder.customer_id,
        orderParams,
        accountId: membershipOrder.account_id,
    })

    try {
        localStorage.setItem('wr_login_data', JSON.stringify(membershipOrder))
    } catch (error) {
        console.error(error)
    }

    state.subscriptionComplete = true
    if (!cart.requireShipping) {
        state.equipmentComplete = true
    }
    return membershipOrder
}

const prePaymentValidator = async(): Promise<boolean> => {
    const isUnique = await isUniqueEmail(store.$state.email)
    if (!isUnique) {
        throw new Error(
            'Your email address is already in use. Please contact support@winreality.com',
        )
    }

    if (!store.$state.accountValidated) {
        // open create account accordion
        store.$state.steps.createAccount.open = true
        await nextTick()
        // scroll to createAccountAccordion ref
        window.scrollTo({
            top: createAccountAccordion.value.$el.offsetTop,
            behavior: 'smooth',
        })
        throw new Error('Please re-validate your account credentials')
    }
    store.$state.username = uuid()
    return true
}

const paymentHandler = async(paymentMethodId: string, stripeCustomerId: string) => {
    state.showProgressModal = true
    setTimeout(() => {
        state.paymentComplete = true
    }, 500)
    try {
        if (!state.subscriptionComplete) {
            await handleSubscriptionPurchase(paymentMethodId, stripeCustomerId)
        }

        if (store.$state.membershipOrder && cart.requireShipping) {
            const stripeCustomerId = store.$state.membershipOrder.customer_id
            const equipmentOrder = await handleEquipmentPurchase(paymentMethodId, stripeCustomerId)
            handleTracking(store.$state.membershipOrder, equipmentOrder)
        } else {
            handleTracking(store.$state.membershipOrder, null)
        }
        const loginResponse = await userStore.login({
            username: store.$state.email,
            password: store.$state.password,
        })
        if (loginResponse?.access_token) {
            localStorage.setItem('access_token', loginResponse.access_token)
        }
        userStore.userInfo()
        await finalizeOrder()
        if (cart.headsetFinancing.enabled) await handleHeadsetFinancing()
        return true
    } catch (e: any) {
        resetProgress()
        if (e?.message && typeof e.message === 'string') {
            try {
                const message = JSON.parse(e.message || '{}')
                if (message?.message) {
                    throw new Error(message.message)
                }
            } catch (error) {
                //throw e
            }
        } else if (e?.message && typeof e.message !== 'string') {
            console.error(e)
            throw new Error('Something went wrong. Please contact support')
        }
        throw e
    }
}

const handleHeadsetFinancing = async() => {
    const fields = [
        new Date().toUTCString(),
        store.org?.name,
        store.fullName,
        store.email,
        cart.headsetFinancing.paymentOption,
    ]
    const formData = new FormData()

    formData.append('action', 'append_to_financing_sheet')
    formData.append('fields', JSON.stringify(fields))

    try {
        const response = await fetch('https://winreality.com/wp-admin/admin-ajax.php', {
            method: 'POST',
            body: formData,
        })

        if (!response.ok) {
            throw new Error('Network response was not ok')
        }

        const data = await response.json()
        console.log('Success:', data)
    } catch (error) {
        console.error('Error:', error)
    }
}
</script>
